import * as React from 'react';
import { SpinnerWrapper } from 'soprasteria-ui-library';
import { useAuth } from '../../App';
import { NotificationsApi } from "../../api/NotificationsApi";
import { INotifications } from '../../models/INotifications';
import { ReferralNotifications } from './ReferralNotifications';
import { OrthodonticReferralNotifications } from './OrthodonticReferralNotifications';
import { GeneralDentalReferralNotifications } from './GeneralDentalReferralNotifications';
import { UserContext, isInRole } from '../../contexts/UserContext';
import { SPECIALISTTANDVÅRDBAS, ALLMÄNTANDVÅRDBAS, URVALSTANDLÄKAREBAS, TANDREGLERINGBAS, HANDLÄGGAFAKTUROR, ATTESTERAFAKTUROR } from '../../utils/RoleConstants';
import { InvoiceNotifications } from './InvoiceNotifications';
import { OrthodonticLkgCfPatientExtensionNotifications } from './OrthodonticLkgCfPatientExtensionNotifications';
import { RiskPotReportNotifications } from "./RiskPotReportNotifications";

export const Notifications: React.FunctionComponent<{}> = () => {

    const auth = useAuth();
    const api = new NotificationsApi(auth)
    const [isFetchingNotifications, setIsFetchingNotifications] = React.useState<boolean>(false);
    const [notifications, setNotifications] = React.useState<INotifications>();
    const userInfo = React.useContext(UserContext);
    const hasRoleSpecialist = isInRole(SPECIALISTTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleGeneralDentist = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleConsultant = isInRole(URVALSTANDLÄKAREBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleOrtodontist = isInRole(TANDREGLERINGBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleInvoiceApprover = isInRole(HANDLÄGGAFAKTUROR, userInfo.roles ? userInfo.roles : []);
    const hasRoleInvoiceCerifier = isInRole(ATTESTERAFAKTUROR, userInfo.roles ? userInfo.roles : []);
    const [showNotifications, setShowNotifications] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (auth.userRolesGroup && (hasRoleSpecialist || hasRoleGeneralDentist || hasRoleConsultant || hasRoleOrtodontist || hasRoleInvoiceApprover || hasRoleInvoiceCerifier)) {
            getNotifications();
        }
    }, [auth.userRolesGroup, hasRoleSpecialist, hasRoleGeneralDentist, hasRoleConsultant, hasRoleOrtodontist, hasRoleInvoiceApprover, hasRoleInvoiceCerifier])

    React.useEffect(() => {
        if (notifications &&
            (
                notifications.numberOfSpecialistReferralsInNeedOfProcessing > 0 ||
                (notifications.sentInvoices && notifications.sentInvoices.amount > 0) ||
                (notifications.approvedInvoices && notifications.approvedInvoices.amount > 0) ||
                (notifications.orthodonticReferralsWithAwatingDecision && notifications.orthodonticReferralsWithAwatingDecision.amount > 0) ||
                (notifications.orthodonticReferralsWithRequestedCompletion && notifications.orthodonticReferralsWithRequestedCompletion.amount > 0) ||
                notifications.numberOfOrthodonticReferralsWithRequestedPatientViewing > 0 ||
                (notifications.generalDentalReferralsWithAwaitingApproval && notifications.generalDentalReferralsWithAwaitingApproval.amount > 0) ||
                (notifications.generalDentalReferralsWithRequestedCompletion && notifications.generalDentalReferralsWithRequestedCompletion.amount > 0) ||
                (notifications.generalDentalReferralsWithUnreadAnswer && notifications.generalDentalReferralsWithUnreadAnswer.amount > 0) ||
                (notifications.specialistReferralsWithAwaitingApproval && notifications.specialistReferralsWithAwaitingApproval.amount > 0) ||
                notifications.othodonticReferralsToShare > 0 ||
                notifications.orthodonticLkgcfPatientExtensionAwaitingDecision && notifications.orthodonticLkgcfPatientExtensionAwaitingDecision.amount > 0 ||
                notifications.numberOfSpecialistReferralsToShare > 0 ||
                (notifications.specialistReferralsWithUnreadAnswer && notifications.specialistReferralsWithUnreadAnswer.amount > 0) ||
                notifications.orthodonticReferralsStartedInAnotherCounty && notifications.orthodonticReferralsStartedInAnotherCounty.amount > 0 ||
                (notifications.closedSpecialistReferralsWithUnreadAnswer && notifications.closedSpecialistReferralsWithUnreadAnswer.amount > 0) ||
                (notifications.orthodonticReferralsWithUnreadAnswer && notifications.orthodonticReferralsWithUnreadAnswer.amount > 0) ||
                notifications.numberOfRiskPotReportsWithUnreadPreAssessmentDecision > 0 ||
                (notifications.specialistReferralsWithRequestedCompletion && notifications.specialistReferralsWithRequestedCompletion.amount > 0) ||
                (notifications.rejectedSpecialistReferralsWithUnreadAnswer && notifications.rejectedSpecialistReferralsWithUnreadAnswer.amount > 0)
            )) {
            setShowNotifications(true);
        }
        else {
            setShowNotifications(false);
        }
    }, [notifications])

    const getNotifications = () => {
        setIsFetchingNotifications(true);
        api.getNotifications((newNotifications) => {
            setNotifications(newNotifications);
        }, undefined, () => setIsFetchingNotifications(false));
    }

    return (
        <>
            {
                hasRoleSpecialist || hasRoleGeneralDentist || hasRoleConsultant || hasRoleOrtodontist || hasRoleInvoiceApprover ?
                    <SpinnerWrapper isLoading={isFetchingNotifications}>
                        <section className="card notifications__wrapper" >
                            <div className="card-header">
                                <h2>Notiser</h2>
                            </div>
                            <div className="card-body">
                                {
                                    showNotifications || isFetchingNotifications ?
                                        <>
                                            <InvoiceNotifications
                                                sentInvoices={notifications?.sentInvoices}
                                                approvedInvoices={notifications?.approvedInvoices} />
                                            <ReferralNotifications numberOfSpecialistReferralsInNeedOfProcessing={notifications?.numberOfSpecialistReferralsInNeedOfProcessing ?? 0}
                                                numberOfSpecialistReferralsToShare={notifications?.numberOfSpecialistReferralsToShare ?? 0}
                                                specialistReferralsWithAwaitingApproval={notifications?.specialistReferralsWithAwaitingApproval}
                                                userCareProviderId={userInfo.clinicId}
                                                closedSpecialistReferralsWithUnreadAnswer={notifications?.closedSpecialistReferralsWithUnreadAnswer}
                                                rejectedSpecialistReferralsWithUnreadAnswer={notifications?.rejectedSpecialistReferralsWithUnreadAnswer}
                                                specialistReferralsWithRequestedCompletion={notifications?.specialistReferralsWithRequestedCompletion}/>
                                            <OrthodonticReferralNotifications
                                                orthodonticReferralsWithAwatingDecision={notifications?.orthodonticReferralsWithAwatingDecision}
                                                orthodonticReferralsWithRequestedCompletion={notifications?.orthodonticReferralsWithRequestedCompletion}
                                                numberOfOrthodonticReferralsWithRequestedPatientViewing={notifications?.numberOfOrthodonticReferralsWithRequestedPatientViewing ?? 0}
                                                orthodonticReferralsToShare={notifications?.othodonticReferralsToShare ?? 0}
                                                orthodonticReferralsStartedInAnotherCounty={notifications?.orthodonticReferralsStartedInAnotherCounty}
                                                orthodonticReferralsWithUnreadAnswer={notifications?.orthodonticReferralsWithUnreadAnswer} />
                                            <GeneralDentalReferralNotifications
                                                generalDentalReferralsWithAwatingApproval={notifications?.generalDentalReferralsWithAwaitingApproval}
                                                generalDentalReferralsWithRequestedCompletion={notifications?.generalDentalReferralsWithRequestedCompletion}
                                                generalDentalReferralsWithUnreadAnswer={notifications?.generalDentalReferralsWithUnreadAnswer} />
                                            <OrthodonticLkgCfPatientExtensionNotifications 
                                                orthodonticLkgcfPatientExtensionAwaitingDecision={notifications?.orthodonticLkgcfPatientExtensionAwaitingDecision} />
                                            <RiskPotReportNotifications
                                                numberOfRiskPotReportsWithUnreadPreAssessmentDecision={notifications?.numberOfRiskPotReportsWithUnreadPreAssessmentDecision ?? 0} />

                                        </>
                                        : <p>Det finns inga notifikationer</p>
                                }
                            </div>
                        </section>
                    </SpinnerWrapper>
                    : null
            }
        </>
    )
}